import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { TooltipProvider } from '../ui/tooltip';
import { Plus } from 'lucide-react';
import { useSharedPermissions } from '../../contexts/PermissionsContext';
import { type CampaignDashboardProps, type CampaignFilters, type CampaignStats } from './types';
import { useCampaignData } from './useCampaignData';
import { useCampaignBrief } from './useCampaignBrief';
import { CampaignStatsCards } from './CampaignStatsCards';
import { CampaignFiltersBar } from './CampaignFiltersBar';
import { CampaignOverviewTab } from './CampaignOverviewTab';
import { CampaignListViewTab } from './CampaignListViewTab';
import { CampaignTimeline } from './CampaignTimeline';
import { CampaignBriefDialog } from './CampaignBriefDialog';

export default function CampaignDashboard({ onNavigate }: CampaignDashboardProps) {
    const permissions = useSharedPermissions();

    // Data hooks
    const {
        campaigns,
        loading,
        error,
        brands,
        conceptOptions,
        recentActivity,
        handleDeleteCampaign,
        setError
    } = useCampaignData();

    // Brief dialog hook
    const {
        isBriefDialogOpen,
        briefCampaign,
        briefContent,
        briefPdfUrl,
        briefError,
        isGeneratingBrief,
        briefCopied,
        handleCloseBriefDialog,
        handleGenerateBrief,
        handleCopyBrief
    } = useCampaignBrief();

    // Local state
    const [filters, setFilters] = useState<CampaignFilters>({
        businessUnit: 'All',
        category: 'All Categories',
        concept: 'All Concepts',
        brandId: 'all',
        year: '2025'
    });
    const [activeTab, setActiveTab] = useState('timeline');
    const [collapsedUnits, setCollapsedUnits] = useState<Record<string, boolean>>({});

    // Computed values
    const stats: CampaignStats = useMemo(() => ({
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter(c => c.status === 'active').length,
        businessUnits: [...new Set(campaigns.map(c => c.businessUnit))].length,
        categories: [...new Set(campaigns.map(c => c.category))].length
    }), [campaigns]);

    const filteredCampaigns = useMemo(() => {
        return campaigns.filter(campaign => {
            const matchesBrand = filters.brandId === 'all' || campaign.brandId === Number(filters.brandId);
            return (
                (filters.businessUnit === 'All' || campaign.businessUnit === filters.businessUnit) &&
                (filters.category === 'All Categories' || campaign.category === filters.category) &&
                (filters.concept === 'All Concepts' || campaign.concept === filters.concept) &&
                matchesBrand &&
                campaign.year === parseInt(filters.year)
            );
        });
    }, [campaigns, filters]);

    // Event handlers
    const handleEdit = (campaignId: string) => {
        onNavigate('edit-campaign', campaignId);
    };

    const handleView = (campaignId: string) => {
        onNavigate('campaign-showcase', campaignId);
    };

    const handleDelete = async (campaignId: number) => {
        await handleDeleteCampaign(campaignId);
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading campaigns...</div>;
    }

    if (error) {
        return <div className="p-8 text-center text-red-500">{error}</div>;
    }

    return (
        <>
            <TooltipProvider>
                <div className="min-h-screen bg-[#f8f5f0] p-6">
                    <div className="max-w-7xl mx-auto space-y-6">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-900">Campaign Planner</h1>
                                <p className="text-gray-600 mt-1">Manage your marketing campaigns across all business units</p>
                            </div>
                            {permissions.canAddCampaigns && (
                                <Button
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={() => onNavigate('create-campaign')}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New Campaign
                                </Button>
                            )}
                        </div>

                        {/* Stats Cards */}
                        <CampaignStatsCards stats={stats} />

                        {/* Main Content */}
                        <Card className="p-4">
                            <CardContent className="p-0">
                                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                                        <TabsList className="grid w-full lg:w-auto grid-cols-3">
                                            <TabsTrigger value="timeline">Timeline</TabsTrigger>
                                            <TabsTrigger value="overview">Overview</TabsTrigger>
                                            <TabsTrigger value="list">List View</TabsTrigger>
                                        </TabsList>

                                        <CampaignFiltersBar
                                            filters={filters}
                                            onFiltersChange={setFilters}
                                            brands={brands}
                                            conceptOptions={conceptOptions}
                                        />
                                    </div>

                                    <TabsContent value="overview" className="mt-0">
                                        <CampaignOverviewTab
                                            campaigns={filteredCampaigns}
                                            recentActivity={recentActivity}
                                            onTabChange={setActiveTab}
                                            onEdit={handleEdit}
                                            onView={handleView}
                                            onDelete={handleDelete}
                                            onGenerateBrief={handleGenerateBrief}
                                            isGeneratingBrief={isGeneratingBrief}
                                            generatingBriefCampaignId={briefCampaign?.id}
                                            canEdit={!!permissions.canUpdateCampaigns}
                                            canDelete={permissions.canDeleteCampaigns}
                                        />
                                    </TabsContent>

                                    <TabsContent value="list" className="mt-0">
                                        <CampaignListViewTab
                                            campaigns={filteredCampaigns}
                                            onEdit={handleEdit}
                                            onView={handleView}
                                            onDelete={handleDelete}
                                            onGenerateBrief={handleGenerateBrief}
                                            isGeneratingBrief={isGeneratingBrief}
                                            generatingBriefCampaignId={briefCampaign?.id}
                                            canEdit={!!permissions.canUpdateCampaigns}
                                            canDelete={permissions.canDeleteCampaigns}
                                        />
                                    </TabsContent>

                                    <TabsContent value="timeline" className="mt-0">
                                        <CampaignTimeline
                                            campaigns={filteredCampaigns}
                                            selectedYear={filters.year}
                                            collapsedUnits={collapsedUnits}
                                        />
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </TooltipProvider>

            {/* Campaign Brief Dialog */}
            <CampaignBriefDialog
                isOpen={isBriefDialogOpen}
                onClose={handleCloseBriefDialog}
                campaign={briefCampaign}
                briefContent={briefContent}
                briefPdfUrl={briefPdfUrl}
                briefError={briefError}
                isGenerating={isGeneratingBrief}
                isCopied={briefCopied}
                onCopy={handleCopyBrief}
            />
        </>
    );
}
